import Button from '../../shared/Buttons/BaseButton';
import RichTextCopy from '../../shared/RichTextCopy/RichTextCopy';
import { DocumentWithEmbeddedEntries, HyperlinkedMediaProps } from 'libs/contentful/types';
import { HeroConsolidatedProps } from '../types';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import CTA, { isValidCta } from 'components/shared/CTA/CTA';
import { calculateOverlayMargin, cn } from '@/utils/styles';
import { getEnrollmentUrl } from '@/utils/linkHelpers';
import { ReactElement } from 'react';
import HyperlinkedMedia from '@/components/shared/HyperlinkedMedia/HyperlinkedMedia';
import { BaseModal, EmailCapture, FocusDisclaimers, PromoCodeInput } from 'components/shared/LazyClientComponents';

type HeroCtaProps = Pick<
  HeroConsolidatedProps,
  | 'allowPromoCodeEntry'
  | 'promoCodeInputButtonText'
  | 'showEmailInput'
  | 'emailInputButtonText'
  | 'cta'
  | 'disclaimerModal'
  | 'scrollCta'
  | 'disclaimers'
  | 'promoCodes'
  | 'imageOverlay'
  | 'sys'
  | 'enrollmentUrl'
> & {
  focusDisclaimers?: DocumentWithEmbeddedEntries;
  defaultMobileMediaPlacement?: boolean;
  placeMobileImageUnderCta?: boolean;
  image?: ReactElement;
  className?: string;
  hyperlinkedMedia?: HyperlinkedMediaProps[];
};

const HeroCTA = ({
  showEmailInput,
  emailInputButtonText,
  cta,
  disclaimerModal,
  scrollCta,
  disclaimers,
  focusDisclaimers,
  allowPromoCodeEntry,
  promoCodeInputButtonText,
  promoCodes,
  imageOverlay,
  defaultMobileMediaPlacement,
  sys,
  enrollmentUrl,
  placeMobileImageUnderCta,
  image,
  hyperlinkedMedia,
  className,
}: HeroCtaProps) => {
  const showEmailInputOnly = showEmailInput && !allowPromoCodeEntry;
  const emailButtonText = emailInputButtonText ?? 'Get started';
  const showCtaOnly = !showEmailInput && cta && isValidCta(cta);
  // fallback to the default enrollment URL if no enrollment URL is provided, it happens when the module is part of an experiment
  const baseEnrollmentUrl = enrollmentUrl ?? getEnrollmentUrl();
  const showFocusDisclaimers = !!(showEmailInputOnly && focusDisclaimers);

  const middleContent = (
    <>
      {showCtaOnly && <CTA cta={cta} />}
      {placeMobileImageUnderCta && image != null && <div className={'sm:hidden'}>{image}</div>}
      {disclaimerModal && (
        <BaseModal
          sys={disclaimerModal.sys}
          title={disclaimerModal.headline}
          body={disclaimerModal.copy}
          openButtonText={disclaimerModal.disclaimerText}
          iconPosition={disclaimerModal.iconPosition}
          iconOnlyLink={disclaimerModal.iconOnlyLink}
        />
      )}
    </>
  );

  return (
    <div
      className={cn(
        'hero-cta-section w-full justify-self-end grow content-end mt-6',
        defaultMobileMediaPlacement && `${calculateOverlayMargin(!!imageOverlay)}`,
        className,
      )}>
      <div className="flex flex-col gap-4">
        {allowPromoCodeEntry && (
          <PromoCodeInput
            sys={sys}
            promoButtonText={promoCodeInputButtonText ?? 'Apply code'}
            emailButtonText={emailButtonText}
            allowedCodes={promoCodes ?? []}
            baseEnrollmentUrl={baseEnrollmentUrl}
          />
        )}
        {showFocusDisclaimers ? (
          <FocusDisclaimers
            focusDisclaimers={focusDisclaimers}
            disclaimers={disclaimers}
            sys={sys}
            buttonText={emailButtonText}
            baseEnrollmentUrl={baseEnrollmentUrl}>
            {middleContent}
          </FocusDisclaimers>
        ) : (
          <>
            {showEmailInputOnly && (
              <EmailCapture sys={sys} buttonText={emailButtonText} baseEnrollmentUrl={baseEnrollmentUrl} />
            )}
            {middleContent}
            {disclaimers && (
              <div
                className="hero-disclaimers font-sans text-legal flex flex-col gap-2 sm:mb-4"
                {...liveUpdateAttrs(sys?.id, 'disclaimers')}>
                <RichTextCopy copy={disclaimers} ySpacing={'none'} />
              </div>
            )}
          </>
        )}
        {hyperlinkedMedia != null && hyperlinkedMedia?.length > 0 && (
          <div className={'flex flex-col sm:flex-row gap-2'}>
            {hyperlinkedMedia.map(item => (
              <HyperlinkedMedia
                image={item.image}
                link={item.link}
                key={Buffer.from(item.image.url).toString('hex')}
                targetHeight={44}
              />
            ))}
          </div>
        )}
        {scrollCta && (
          <div className="pt-4">
            <Button sys={scrollCta?.sys} href={scrollCta.url} variant="outlineScroll" className="w-max">
              {scrollCta.text}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroCTA;
